import { Box, Paper, useMediaQuery } from "@material-ui/core";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import CssGroupButton from "src/components/Button/CssGroupButton";
import BarChart from "src/components/ScoringChart/BarChart";
import CssSelector from "src/components/Selector/CssSelector";
import { FigureTypography } from "src/components/Typography/TitleTypography";
import { NoteTypography } from "src/components/Typography/TitleTypography";
import DetailMonitoringWrapper from "src/components/Wrapper/DetailMonitoringWrapper";
import { MONITORING } from "src/config/constants";
import { useUserAnalysisChartSelector, useUserAnalysisSelector } from "src/context/monitoring/userAnalysisContext";
import { formatAddress, numberWithCommas } from "src/services/utils";

const TYPE = {
  TVL: "tvl",
  DEPOSIT: "deposit",
  BORROW: "borrow",
};

const getOption = () => ({
  chart: {
    height: 300,
  },
  xAxis: {
    labels: {
      formatter: function () {
        return `<span>${formatAddress(this.value)}</span>`;
      },
    },
  },
  yAxis: {
    tickAmount: 6,
    labels: {
      enabled: true,
    },
  },
  tooltip: {
    formatter: function () {
      return this.points.reduce(function (s, point) {
        return s + "<br/>" + point.series.name + ": " + numberWithCommas(point.y);
      }, "<b>" + formatAddress(this.x) + "</b>");
    },
    shared: true,
  },
});

export default function TopUsers() {
  const { pool } = useParams();
  const match = useMediaQuery((theme) => theme.breakpoints.only("xs"));
  const { fetchTopUsers } = useUserAnalysisSelector((state) => state.fetch);
  const topUsers = useUserAnalysisChartSelector(MONITORING.ANALYSIS.topUsers, pool);
  const [type, setType] = useState(TYPE.TVL);

  const subTypeConfig = [
    { label: "Deposit", onClick: () => setType(TYPE.DEPOSIT) },
    { label: "Borrow", onClick: () => setType(TYPE.BORROW) },
    { label: "Total Value Lock", onClick: () => setType(TYPE.TVL) },
  ];

  const { value, active } = useMemo(() => {
    if (type === TYPE.TVL) return { value: "Total Value Lock", active: 2 };
    else if (type === TYPE.DEPOSIT) return { value: "Deposit", active: 0 };
    else return { value: "Borrow", active: 1 };
  }, [type]);

  useEffect(() => {
    fetchTopUsers(pool);
  }, [pool]);

  const { categories, series, maxValue, isLoading } = useMemo(() => {
    if (topUsers && topUsers[type]) {
      const { categories, data, maxValue } = topUsers[type];
      return {
        isLoading: false,
        maxValue: maxValue,
        categories: [].concat(categories),
        series: [
          {
            name: `Top users(${type})`,
            data: [].concat(data),
          },
        ],
      };
    }
    return {
      isLoading: true,
      maxValue: 0,
      categories: [],
      series: [],
    };
  }, [topUsers, type]);

  return (
    <DetailMonitoringWrapper
      navigation={{ text: "user analysis", name: "top users", link: `/user-analysis/${pool}` }}
      selectLink="/user-analysis/top-users-by-transaction-volume"
      Component={{
        Description: (
          <NoteTypography>
            The charts show the top 10 users based on their total value locked, deposit, or borrow in a lending pool.
            This data indicates the financial potential of these users in the lending pool and compares their financial
            status.
          </NoteTypography>
        ),
      }}
    >
      <Paper>
        {match ? (
          <CssSelector
            style={{ width: "100%", marginBottom: "1rem" }}
            config={subTypeConfig}
            value={value}
            event={{ onChooseItem: (_, item) => item.onClick() }}
          />
        ) : (
          <CssGroupButton
            style={{ marginBottom: "1rem" }}
            fullWidth={match}
            defaultActive={active}
            config={subTypeConfig}
          />
        )}
        <Box display="flex" justifyContent="flex-start" mb={2}>
          <FigureTypography value={numberWithCommas(maxValue)} title="Total Value Lock" />
        </Box>
        <BarChart isLoading={isLoading} categories={categories} series={series} option={getOption()} />
      </Paper>
    </DetailMonitoringWrapper>
  );
}
